import React, { Fragment, ReactElement, useState } from "react";
import { useDebounce } from "use-debounce";

import { Chip, IconButton, Stack, TextField, Typography } from "@mui/material";
import { Autocomplete } from "@mui/material";

import { useSelector } from "../store";
import { useListPoliciesQuery } from "../store/enhancedApi";
import { EntityInfo, Route } from "../store/pomeriumApi";
import AddPolicyDialog from "../views/policies/components/AddPolicyDialog";
import { CirclePlus } from "./icons";
import Link from "./styled/Link";

type PolicyPickerProps = {
  readonly: boolean;
  policies: EntityInfo[];
  onChange: (policies: EntityInfo[]) => void;
  route?: Route;
  showAdd?: boolean;
};
const PolicyPicker = ({
  readonly,
  policies,
  onChange,
  route,
  showAdd,
}: PolicyPickerProps) => {
  const { clusterId, organizationId } = useSelector((state) => state.current);
  const [inputValue, setInputValue] = useState("");
  const [delayedInputValue, { isPending }] = useDebounce(inputValue, 250);
  const [showAddPolicyDialog, setShowAddPolicyDialog] = useState(false);

  const { data: result, isLoading } = useListPoliciesQuery(
    {
      organizationId,
      namespaceId: clusterId,
      includeDescendants: true,
      limit: 10,
      filter: JSON.stringify({
        items: [
          { field: "name", operator: "contains", value: delayedInputValue },
          { field: "enforced", operator: "is", value: false },
        ],
      }),
    },
    { skip: !organizationId || !clusterId },
  );
  const options: EntityInfo[] =
    result?.rows.map((p) => ({
      id: p.id,
      name: p.name,
      updatedAt: p.updatedAt,
    })) || [];

  const handleChange = (_: unknown, newPolicies: EntityInfo[]) =>
    onChange(newPolicies);
  const handleInputChange = (_: unknown, newInputValue: string) =>
    setInputValue(newInputValue);
  const handleAddPolicyDialogCancel = () => setShowAddPolicyDialog(false);
  const handleAddPolicyDialogAdd = (policy: EntityInfo): void => {
    onChange([...policies, policy]);
    setShowAddPolicyDialog(false);
  };
  const handleClickAddPolicy = () => setShowAddPolicyDialog(true);

  return (
    <>
      <AddPolicyDialog
        open={showAddPolicyDialog}
        onAdd={handleAddPolicyDialogAdd}
        onCancel={handleAddPolicyDialogCancel}
      />
      <Stack gap={1}>
        <Stack direction="row" alignItems="center">
          <Autocomplete
            fullWidth
            disabled={readonly}
            size="small"
            multiple
            loading={isLoading || isPending()}
            freeSolo={false}
            value={policies}
            options={options}
            filterOptions={(x) => x}
            getOptionLabel={(policy: EntityInfo): string => policy.name}
            isOptionEqualToValue={(
              policy: EntityInfo,
              value: EntityInfo,
            ): boolean => policy && value && policy.id === value.id}
            renderInput={(params): ReactElement => (
              <TextField {...params} sx={{ p: 0 }} />
            )}
            noOptionsText={
              <>
                No Policies Available.{" "}
                <Link
                  to={
                    `/app/management/policies/create?oid=` +
                    organizationId +
                    `&cid=` +
                    clusterId
                  }
                  target="_blank"
                >
                  Create One
                </Link>
              </>
            }
            onChange={handleChange}
            onInputChange={handleInputChange}
            sx={{
              "& .MuiFilledInput-root": {
                py: "6px !important",
              },
            }}
          />
          {showAdd && (
            <IconButton onClick={handleClickAddPolicy}>
              <CirclePlus />
            </IconButton>
          )}
        </Stack>
        {(route?.enforcedPolicies?.length || 0) > 0 && (
          <Stack direction="row" alignItems="center" gap={1}>
            <Typography variant="caption">Enforced Policies:</Typography>
            <div>
              {route?.enforcedPolicies.map((p) => (
                <Fragment key={p.id}>
                  <Chip
                    size="small"
                    label={p.name}
                    component={Link}
                    to={`/app/management/policies/${p.id}/edit`}
                    clickable
                  />{" "}
                </Fragment>
              ))}
            </div>
          </Stack>
        )}
      </Stack>
    </>
  );
};
export default PolicyPicker;
